/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Link, useSearchParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import ArgosButton from "../../../components/Buttons/ArgosButton";
import toast from "react-hot-toast";
import {usePostRequest} from "../../../../api/helper";
import ArgosTextField from "../../../components/Form/TextField/ArgosTextField";
import {Box, TextField, Typography} from '@mui/material';
import {emptyObject, Note, ObjectDef, updateObjectEndpoint, versionsTabUrlParam} from "../constants";
import {useHasWriteAccess} from "../../../../redux/ducks/AccessControl/accessControlHelpers";

interface ContentProps {
  object: ObjectDef
}

const Content = (props: ContentProps) => {
  const [object, setObject] = useState<ObjectDef>(props.object ?? emptyObject)
  const [searchParams, _] = useSearchParams()
  const id = searchParams.get(versionsTabUrlParam)
  const addObjHandle = usePostRequest(updateObjectEndpoint)
  const hasWriteAccess = useHasWriteAccess();
  /*
    const map: any = {
      "Image (N/A)": "0",
      "Width (m)": "1",
      "Height (m)": "2",
      "Length (m)": "3",
      "RCS (dBsm)": "4",
      "Retroreflectivity (cd/lx/m²)": "5",
      //"Camera Detection Requirement - Pixels Across Short Dimension": "pixelsAcrossShortDimension",
      //"LiDAR Detection Requiment - Points on Object": "pointsOnObject",
      "Notes": "comments.comments",
      "Link": "comments.link"
    }*/
  const keyToLabelMap: any = {
    "0": "Image (N/A)",
    "1": "Width (m)",
    "2": "Height (m)",
    "3": "Length (m)",
    "4": "Orientation Width (m)",
    "5": "Orientation Height (m)",
    "6": "RCS (dBsm)",
    "7": "Retroreflectivity (cd/lx/m²)",
    "retroreflectivity": "Retroreflectivity (cd/lx/m²)",
    "rcs": "RCS (dBsm)",
    "widthHorizontal": "Width (m)",
    "heightVertical": "Height (m)",
    "oWidth": "Orientation Width (m)",
    "oHeight": "Orientation Height (m)",
    "length": "Length (m)",
    //"pixelsAcrossShortDimension": "Camera Detection Requirement - Pixels Across Short Dimension",
    //"pointsOnObject": "LiDAR Detection Requiment - Points on Object",
    "comments.comments": "Notes",
    "comments.link": "Link"
  }
  const keyToFieldMap: any = {
    //"0": "Image (N/A)",
    "1": "widthHorizontal",
    "2": "heightVertical",
    "3": "length",
    "4": "oWidth",
    "5": "oHeight",
    "6": "rcs",
    "7": "retroreflectivity",

  }
  useEffect(() => {
    let tempObj: any = {}
    Object.keys(props.object.table).map((key, index) => {
      if (props.object.type == "Custom" && key == "0")
        return;
      const field = keyToFieldMap[key]
      tempObj[field] = props.object.table[parseInt(key)].value

    })
    setObject({...props.object, ...tempObj})
  }, [props.object])

  const handleAdd = () => {
    if (!id) {
      toast.error('invalid object id')
      return
    }
    // add object to database
    addObjHandle(object).then(() => {
      toast.success('object updated successfully')
    }).catch((err) => {
      // TODO: handle error (revert?)
      toast.error('error updating object')
      console.error(err)
    })
  }


  const noteText = object?.notes?.note ?? ""
  const noteUrl = object?.notes?.url ?? ""

  return (
    <div style={{overflow: 'auto', height: '100%', display: 'flex'}}>
      <div style={{overflow: 'auto', display: 'flex', flexDirection: 'column'}}>
        {Object.keys(object.type == "Custom" ? object : object.table).map((key, index) => {
            if (object.type == "Custom" && key == "0")
              return;

            const label = keyToLabelMap[key]
            if (!label) return null
            return (
              <div style={{height: 100}}>
                {(label != "Notes" && label != "Link") &&
                  <div style={{display: "flex"}}><TextField
                    multiline
                    disabled={object.type !== "Custom" || !hasWriteAccess}
                    rows={1}
                    key={index}
                    label={label}
                    InputProps={{
                      readOnly: object.type !== "Custom" || !hasWriteAccess,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type={'any'}
                    onChange={(e) => setObject({...object, [key]: e.target.value})}
                    sx={{width: '400px', marginLeft: object.type !== "Custom" ? '0px' : '90px', my: '6px'}}
                    value={object.type == "Custom" ? object[key as keyof ObjectDef] : object.table[parseInt(key)].value}
                  />&nbsp;{object.type !== "Custom" &&
                    <TextField
                      multiline
                      rows={1}
                      key={index}
                      label='Comment'
                      InputProps={{
                        readOnly: object.type !== "Custom",
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      type={'any'}
                      onChange={(e) => setObject({...object, [key]: e.target.value})}
                      sx={{width: '400px', marginLeft: object.type !== "Custom" ? '0px' : '90px', my: '6px'}}
                      value={object.table[parseInt(key)].note}
                    />}</div>
                }
              </div>
            )
          }
        )}
        {object.type === "Custom" && <ArgosButton disabled={object.type !== "Custom" || !hasWriteAccess} onClick={() => handleAdd()}>Save</ArgosButton>}
      </div>
      <div style={{overflow: 'auto', paddingLeft: 20, display: 'flex', flexFlow: 'column nowrap'}}>
        <div style={{overflow: 'auto'}}>
          {object.type === "Custom" ?
            <ArgosTextField
              multiline
              rows={19}
              disabled={object.type !== "Custom" || !hasWriteAccess}
              label={'References'}
              type={'text'}
              onChange={(e) => {
                const newNote: Note = {note: e.target.value, url: ""}
                setObject({...object, notes: newNote})
              }}
              sx={{
                minWidth: 300,
                width: '400px',
                // height: 450,
              }}
              value={noteText}
            />
            :
            <Box
              sx={{
                boxShadow: 1,
                border: "1px solid #80808057",
                borderRadius: 2,
                p: 2,
                minWidth: 300,
                width: '400px',
                height: 450
              }}
              key={Object.keys(object).length - 1}
            >
              <Box style={{
                backgroundColor: "whitesmoke",
                color: "rgba(0, 0, 0, 0.6)",
                fontWeight: 400,
                fontSize: "13px",
                justifySelf: "center",
                position: "absolute",
                marginTop: "-26px",
                paddingBottom: "5px",
                paddingLeft: 8,
                paddingRight: 8
              }}
              >
                References:
              </Box>
              {(noteText && !noteUrl) &&
                <Typography>{noteText}</Typography>}
              {(noteText && noteUrl) &&
                <Link to={noteUrl} target="_blank">{noteText}</Link>}
            </Box>
          }
        </div>
      </div>
    </div>
  )
}

export default Content
